import { Config } from 'config/envConfig';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { Table, Tag, Empty } from 'antd';
import { LoadingView } from '@quyva/shared-ui';
import { getUrl } from '../../../pdfDisplay/utils';

/**
 * ViewOrganisationConfiguration — read-only viewer for the uploaded org
 * access-control contacts (the identifier-keyed allowlist).
 *
 * The stored config keys each person under EVERY identifier they carry
 * (email / whatsapp_number / telegram_user_id / telegram_username). The backend
 * (`GET /organization-config/contacts/:orgId`, Node) collapses those back into one row per
 * person and returns `_matched_identifiers`, so admins can confirm who is in the
 * allowlist — and via which channel identifiers — without opening the file on disk.
 * To change the list, admins re-upload (Update Org Data); this view never mutates.
 */
const ViewOrganisationConfiguration = ({ organizationId }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [contacts, setContacts] = useState([]);
    const [meta, setMeta] = useState({ filename: '', identifierCount: 0 });
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!organizationId) return;
        const fetchConfig = async () => {
            try {
                setIsLoading(true);
                setError(null);
                // Node, not FastAPI: the contacts file holds member PII, and this
                // route is behind verifyToken + an org-admin check there.
                const url = getUrl(
                    `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/organization-config/contacts/${organizationId}`
                );
                // Token read fresh from localStorage (where the refresh writes it)
                // so a long-open settings page doesn't send a stale one.
                const token = JSON.parse(
                    localStorage.getItem('current') || 'null'
                )?.token;
                const response = await axios.get(url, {
                    headers: { Authorization: encodeURIComponent(token || '') },
                });
                const data = response.data || {};
                setContacts(Array.isArray(data.contacts) ? data.contacts : []);
                setMeta({
                    filename: data.filename || '',
                    identifierCount: data.identifier_count || 0,
                });
            } catch (err) {
                console.error('Error fetching organization config:', err);
                setError('Failed to load organization data. Please try again.');
            } finally {
                setIsLoading(false);
            }
        };
        fetchConfig();
    }, [organizationId]);

    if (isLoading) {
        return <LoadingView loadingText='Loading organization data...' />;
    }

    if (error) {
        return (
            <div className='p-6 text-center text-texts-danger'>{error}</div>
        );
    }

    if (!contacts.length) {
        return (
            <div className='p-6'>
                <Empty description='No contacts found in the uploaded organization data.' />
            </div>
        );
    }

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (v) => v || <span className='text-texts-secondary'>—</span>,
        },
        {
            title: 'Role',
            dataIndex: 'role',
            key: 'role',
            render: (v) =>
                v ? (
                    <Tag color={v === 'admin' ? 'geekblue' : 'default'}>
                        {v}
                    </Tag>
                ) : (
                    <span className='text-texts-secondary'>—</span>
                ),
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            render: (v) => v || <span className='text-texts-secondary'>—</span>,
        },
        {
            title: 'WhatsApp',
            dataIndex: 'whatsapp_number',
            key: 'whatsapp_number',
            render: (v) => v || <span className='text-texts-secondary'>—</span>,
        },
        {
            title: 'Telegram',
            key: 'telegram',
            render: (_, r) => {
                const id = r.telegram_user_id;
                const uname = r.telegram_username;
                if (!id && !uname)
                    return <span className='text-texts-secondary'>—</span>;
                return (
                    <span>
                        {uname ? `@${uname}` : ''}
                        {uname && id ? ' ' : ''}
                        {id ? (
                            <span className='text-texts-secondary'>
                                ({id})
                            </span>
                        ) : (
                            ''
                        )}
                    </span>
                );
            },
        },
    ];

    const dataSource = contacts.map((c, i) => ({ key: i, ...c }));

    return (
        <div className='w-[90vw] min-w-[90px] md:w-[70vw] md:min-w-[600px] flex-col p-2 overflow-y-auto'>
            <div className='mb-3 px-1 text-sm text-texts-secondary300'>
                {contacts.length} approved contact
                {contacts.length === 1 ? '' : 's'}
                {meta.identifierCount
                    ? ` · ${meta.identifierCount} identifier${
                          meta.identifierCount === 1 ? '' : 's'
                      } in allowlist`
                    : ''}
                {meta.filename ? ` · source: ${meta.filename}` : ''}
            </div>
            <Table
                columns={columns}
                dataSource={dataSource}
                size='small'
                pagination={
                    contacts.length > 10 ? { pageSize: 10 } : false
                }
                scroll={{ x: true }}
            />
            <div className='mt-3 px-1 text-xs text-texts-secondary'>
                Read-only. To change this list, use “Update Org Data” to
                re-upload the contacts file (a re-upload replaces the current
                list).
            </div>
        </div>
    );
};

export default ViewOrganisationConfiguration;
